import { useFormContext, useWatch } from 'react-hook-form';
import { shouldUseDsoStructure } from '~/growthbook';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useIsParentOfficeSelector } from '~/hooks/useIsParentOfficeSelector';
import { type IShiftDetailsFormValues } from '../ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';

interface IPickedChildOffice {
  /** A parent viewing as itself: Shift Details shows it the Office dropdown. */
  isParentView: boolean;
  /** The child that parent picked in the dropdown; null until it picks one, and outside the parent view. */
  pickedChildId: string | null;
}

/**
 * The office a parent's shift request is for (GOTU-14829). The Office dropdown writes only the form's
 * `officeId`, never the store, so the store's selected office stays the parent and a gate that checks
 * the office reads the picked child by this id. Needs the wizard's form context.
 */
export const usePickedChildOffice = (): IPickedChildOffice => {
  const isParentOffice = useIsParentOfficeSelector();
  // Under the DSO structure a multi-office parent that hasn't picked an office has no
  // selectedOfficeId (a single office gets its own id), and the sidebar's Post shift link
  // reaches the wizard without forcing a pick.
  const isDsoParentView = useAppSelector(
    (state) => shouldUseDsoStructure() && !state.user.selectedOfficeId,
  );
  const userId = useAppSelector((state) => state.user.user?.id ?? null);
  const { control } = useFormContext<IShiftDetailsFormValues>();
  const formOfficeId = useWatch({ control, name: 'officeId' });

  const isParentView = isParentOffice || isDsoParentView;
  const pickedChildId =
    isParentView && formOfficeId && formOfficeId !== userId ? formOfficeId : null;

  return { isParentView, pickedChildId };
};
